<template>
  <div id="wd-2" class="py-5 px-3">
    <loading :status="loading"></loading>
    <div class="bg-white">
      <div class="mx-auto my-2">
        <div class="h-full flex">
          <div class="w-full">
            <div class="flex justify-center">
              <div class="w-full max-w-2xl bg-white">
                <button v-if="progress > 32" class="btn text-xs shadow-none border-none" @click.prevent="startAgain">
                  <svg-vue class="w-5 h-5 text-gray-600 hover:text-red-600" icon="font-awesome.times-circle-light"></svg-vue>
                </button>
                <template v-if="$route.query.appearance === 'dedicated'">
                  <img :src="$store.state.settings.icon" :alt="$store.state.settings.app_name" class="mx-auto w-12 h-12 mb-6" />
                </template>
                <div class="mb-2 flex justify-between text-xs">
                  <template v-if="$route.query.appearance === 'dedicated'">
                    <a href="/" class="text-gray-500 hover:text-blue-500"> {{ $t('Go back home') }}</a>
                  </template>
                  <template v-else>
                    <a href="/repair-booking-embed-w2?appearance=dedicated" target="_blank" class="text-gray-500 hover:text-blue-500">
                      {{ $t('Having scrolling trouble ? try here') }}</a
                    >
                  </template>
                  <strong>{{ progress }}%</strong>
                </div>
                <div class="w-full bg-blue-200 rounded-full h-2">
                  <div class="bg-blue-700 h-2 rounded-full" :style="`width: ${progress}%`"></div>
                </div>
                <div class="md:flex justify-between my-1">
                  <button class="btn text-xs shadow-none border-none" :disabled="progress < 16" @click="progress = 16">
                    {{ $t('Brand') }}
                  </button>
                  <button class="btn text-xs shadow-none border-none" :disabled="progress < 32" @click="progress = 32">
                    {{ $t('Device') }}
                  </button>
                  <button class="btn text-xs shadow-none border-none" :disabled="progress < 48" @click="progress = 48">
                    {{ $t('Issues') }}
                  </button>
                  <button class="btn text-xs shadow-none border-none" :disabled="progress < 64" @click="progress = 64">
                    {{ $t('Priority') }}
                  </button>
                  <button class="btn text-xs shadow-none border-none" :disabled="progress < 87" @click="progress = 87">
                    {{ $t('Complete') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mx-auto my-2">
        <div class="w-full">
          <div class="flex justify-center">
            <div class="w-full py-3 max-w-lg bg-white">
              <template v-if="progress == 16">
                <p class="uppercase text-gary-400 mb-3 text-sm text-center">{{ $t('Select your device brand') }}</p>
                <template v-if="brandList.length">
                  <div
                    v-for="(brand, index) in brandList"
                    :key="index"
                    @click="getDevices(brand)"
                    class="flex items-center px-6 py-3 border border-blue-400 rounded-md mb-1 hover:bg-gray-100 cursor-pointer"
                  >
                    <div>
                      <div class="flex items-center justify-center">
                        <svg-vue
                          class="w-5 h-5 text-green-600"
                          icon="font-awesome.check-circle-solid"
                          v-if="repair.brand_id == brand.id"
                        ></svg-vue>
                        <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                      </div>
                    </div>
                    <div class="flex flex-col ml-6">
                      <p class="text-sm leading-5">
                        {{ brand.name }}
                      </p>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <no-content />
                </template>
              </template>
              <template v-if="progress == 32">
                <p class="uppercase text-gary-400 text-sm text-center">{{ $t('Select your device model') }}</p>
                <div :style="{ maxHeight: '350px' }" class="my-6 flex p-2 flex-col overflow-y-auto">
                  <template v-if="deviceList.length">
                    <div
                      v-for="(device, index) in deviceList"
                      :key="index"
                      @click="getDefects(device)"
                      class="flex items-center px-6 py-3 border border-blue-400 rounded-md mb-1 hover:bg-gray-100 cursor-pointer"
                    >
                      <div>
                        <div class="flex items-center justify-center">
                          <svg-vue
                            class="w-5 h-5 text-green-600"
                            icon="font-awesome.check-circle-solid"
                            v-if="repair.device_id == device.id"
                          ></svg-vue>
                          <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                        </div>
                      </div>
                      <div class="flex flex-col ml-6">
                        <p class="text-sm leading-5">
                          {{ device.name }}
                        </p>
                        <p
                          class="text-xs leading-4 text-gray-700 group-hover:text-gray-700 group-focus:underline transition ease-in-out duration-150"
                        >
                          {{ device.model }}
                        </p>
                      </div>
                      <div class="ml-auto">
                        <img :alt="device.name" :src="device.avatar" class="inline-block rounded-full h-8 w-8" />
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <no-content />
                  </template>
                </div>
                <div class="flex justify-between my-6">
                  <button class="btn btn-secondary text-xs" @click.prevent="startAgain">{{ $t('Back') }}</button>
                </div>
              </template>
              <template v-if="progress == 48">
                <p class="uppercase text-gary-400 text-sm text-center">{{ $t('Issues to be repaired') }}</p>
                <div :style="{ maxHeight: '350px' }" class="my-6 flex p-2 flex-col overflow-y-auto">
                  <template v-if="defectList.length">
                    <div
                      v-for="(defect, index) in defectList"
                      :key="index"
                      @click="selectDefect(defect)"
                      class="flex items-center px-6 py-3 border border-blue-400 rounded-md mb-1 hover:bg-gray-100 cursor-pointer"
                    >
                      <div>
                        <div class="flex items-center justify-center">
                          <svg-vue
                            class="w-5 h-5 text-green-600"
                            icon="font-awesome.check-circle-solid"
                            v-if="currentDefects.includes(defect)"
                          ></svg-vue>
                          <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                        </div>
                      </div>
                      <div class="flex flex-col ml-6">
                        <p class="text-sm leading-5">
                          {{ defect.title }}
                        </p>
                        <p
                          class="text-xs leading-4 text-gray-700 group-hover:text-gray-700 group-focus:underline transition ease-in-out duration-150"
                        >
                          {{ $t('Required Time') }} :
                          {{ defect.required_time }}
                        </p>
                      </div>
                      <div class="ml-auto">
                        <small>{{ defect.price | currency }}</small>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <no-content />
                  </template>
                </div>
                <div class="flex justify-between my-6">
                  <button class="btn btn-secondary text-xs" @click.prevent="startAgain">{{ $t('Back') }}</button>

                  <button class="btn btn-app px-10" type="button" v-if="currentDefects.length" @click.prevent="progress = 64">
                    {{ $t('Next') }}
                  </button>
                </div>
              </template>
              <template v-if="progress == 64">
                <p class="uppercase text-gary-400 text-sm text-center">{{ $t('Repair processing priority') }}</p>
                <div :style="{ maxHeight: '350px' }" class="my-6 flex flex-col overflow-y-auto">
                  <template v-for="(priority, index) in priorityList">
                    <div
                      :key="index"
                      @click="selectPriority(priority)"
                      class="flex items-center px-6 py-3 border border-blue-400 rounded-md mb-1 hover:bg-gray-100 cursor-pointer"
                    >
                      <div>
                        <div class="flex items-center justify-center">
                          <svg-vue
                            class="w-5 h-5 text-green-600"
                            icon="font-awesome.check-circle-solid"
                            v-if="repair.repair_priority_id === priority.id"
                          ></svg-vue>
                          <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                        </div>
                      </div>
                      <div class="flex flex-col ml-6">
                        <p class="text-sm leading-5">
                          {{ priority.name }}
                        </p>
                      </div>
                      <div class="ml-auto">
                        <small>{{ priority.extra_charge | currency }}</small>
                      </div>
                    </div>
                  </template>
                </div>
                <div class="flex justify-between my-5">
                  <button class="btn btn-secondary text-xs" @click.prevent="startAgain">{{ $t('Back') }}</button>

                  <button class="btn btn-app px-10" type="button" v-if="repair.repair_priority_id" @click.prevent="progress = 87">
                    {{ $t('Next') }}
                  </button>
                </div>
              </template>
              <div :class="progress > 86 ? '' : 'hidden'">
                <p class="uppercase text-gary-400 text-sm text-center">{{ $t('Your information') }}</p>
                <div class="mt-6 flex flex-col md:flex-row">
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="name">
                      {{ $t('Name') }}
                    </label>
                    <div class="pt-1">
                      <input id="name" :placeholder="$t('Name')" class="form-input" required type="text" v-model="repair.name" />
                    </div>
                  </div>
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="email">
                      {{ $t('Email address') }}
                    </label>
                    <div class="pt-1">
                      <input
                        id="email"
                        :placeholder="$t('Email address')"
                        class="form-input"
                        required
                        type="email"
                        v-model="repair.email"
                      />
                    </div>
                  </div>
                </div>
                <div class="flex flex-col md:flex-row">
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="phone">
                      {{ $t('Phone') }}
                    </label>
                    <div class="pt-1">
                      <input :placeholder="$t('Phone')" class="form-input" type="tel" id="phone" v-model="repair.phone" />
                    </div>
                  </div>
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="serial_number">
                      {{ $t('Serial number') }}
                    </label>
                    <div class="pt-1">
                      <input id="serial_number" :placeholder="$t('Serial number')" class="form-input" v-model="repair.serial_number" />
                    </div>
                  </div>
                </div>

                <div class="flex flex-col md:flex-row">
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="address">
                      {{ $t('Your address') }}
                    </label>
                    <div class="pt-1">
                      <template v-if="repair.postal_code">
                        <select v-model="repair.address" class="form-input bg-white">
                          <option :value="null">{{ $t('Select an option') }}</option>
                          <option v-for="(address, index) in addresses" :key="index" :value="`${address},${postalCode}`">
                            {{ `${address},${postalCode}` | capitalize }}
                          </option>
                        </select>
                      </template>
                      <template v-else>
                        <input id="address" :placeholder="$t('Home address')" class="form-input" v-model="repair.address" />
                      </template>
                    </div>
                  </div>
                </div>
                <div class="flex flex-col md:flex-row">
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="diagnostics">
                      {{ $t('Note') }}
                    </label>
                    <div class="pt-1">
                      <input-wysiwyg id="diagnostics" v-model="repair.diagnostics"></input-wysiwyg>
                    </div>
                  </div>
                </div>
                <div class="flex flex-col md:flex-row">
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="has_warranty">{{ $t('Device warranty status') }}</label>
                    <div class="pt-1">
                      <input-switch
                        :disabled-label="$t('No')"
                        :enabled-label="$t('Yes')"
                        id="has_warranty"
                        v-model="repair.has_warranty"
                      ></input-switch>
                      <div class="form-description-text">{{ $t('This option will set device has warranty or not') }}.</div>
                    </div>
                  </div>
                </div>

                <template v-show="currentDefects.length">
                  <div class="text-right px-2 pt-3 sm:px-4">
                    <strong>{{ $t('Subtotal') }}</strong>
                    : {{ subTotalAmount | currency }}
                    <br />
                    <strong>{{ $t('Priority Process Charges') }}</strong>
                    : {{ currentPriority.extra_charge | currency }}
                    <br />
                    <strong>{{ $t('Tax') }}</strong>
                    : {{ taxAmount | currency }}
                    <br />
                    <strong>{{ $t('Total Amount') }}</strong>
                    : {{ totalAmountWithTax | currency }}
                  </div>

                  <payment_gateways @start="loading = true" @stop="loading = false" @process="saveRepairOrder($event)">
                    <button class="btn btn-app mr-4" @click.prevent="startAgain">{{ $t('Back') }}</button>
                  </payment_gateways>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Gateways from '@/components/payments/gateways';

export default {
  name: 'booking',
  metaInfo() {
    return {
      titleTemplate: `%s - ${this.$store.state.settings.name}`,
    };
  },
  data() {
    return {
      postalCode: null,
      isVerifiedPostalCode: false,
      isCollection: false,
      loading: false,
      addresses: [],
      progress: 0,
      deviceList: [],
      defectList: [],
      priorityList: [],
      currentDefects: [],
      currentPriority: {
        extra_charge: 0,
      },
      brandList: [],
      device_type_id: null,
      tax: {
        tax_rate: 0,
        is_tax_fix: false,
        is_tax_included: false,
      },
      repair: {
        has_warranty: false,
        brand_id: null,
        device_id: null,
        defects: [],
        serial_number: null,
        name: null,
        email: null,
        phone: null,
        address: null,
        diagnostics: null,
        repair_priority_id: 1,
        sub_total: 0,
        total_cost: 0,
        profit: 0,
        tax: 0,
        total_charges: 0,
        device_type_id: null,
        postal_code: null,
      },
    };
  },
  components: {
    payment_gateways: Gateways,
  },
  computed: {
    isAceptingRepair() {
      return this.$store.state.settings.is_acepting_repair;
    },
    repairCostAmount() {
      let amount = 0;
      this.currentDefects.forEach((defect) => {
        amount += defect.cost;
      });
      return amount;
    },
    subTotalAmount() {
      let amount = 0;
      this.currentDefects.forEach((defect) => {
        amount += defect.price;
      });
      return amount;
    },
    profit() {
      return Number(this.totalAmountWithTax - this.repairCostAmount - this.taxAmount);
    },
    subTotalWithPriority() {
      const amount = Number(this.subTotalAmount) + Number(this.currentPriority.extra_charge);
      return amount;
    },
    taxAmount() {
      const amount = this.subTotalWithPriority;
      const rate = Number(this.tax.tax_rate ?? this.tax.rate);
      if (this.tax.is_vat) {
        if (this.tax.is_tax_included) {
          const vatAmount = amount - amount / (1 + rate / 100);
          return vatAmount.toFixed(2);
        } else {
          const vatAmount = amount * (rate / 100);
          return vatAmount.toFixed(2);
        }
      } else {
        if (this.tax.is_tax_fix) {
          return rate;
        }
        return amount * (rate / 100);
      }
    },
    totalAmountWithTax() {
      if (this.tax.is_tax_included) {
        return this.subTotalWithPriority;
      }
      return this.subTotalWithPriority + Number(this.taxAmount);
    },
  },
  mounted() {
    this.bookingInit();
  },
  methods: {
    startAgain() {
      this.currentDefects = [];
      this.currentBrand = {};
      this.progress = 16;
    },
    checkPostalCode() {
      this.loading = true;
      axios
        .post(route('repair.check-postal-code'), {
          postal_code: this.postalCode,
        })
        .then((response) => {
          this.loading = false;
          this.addresses = response.data.code.addresses;
          this.repair.postal_code = response.data.code.code;
          this.isVerifiedPostalCode = true;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    saveRepairOrder(gateway) {
      this.loading = true;
      this.repair.payment_gateway_key = gateway.key;
      this.repair.payment_gateway_value = gateway.value;
      this.repair.sub_total = this.subTotalAmount;
      this.repair.tax = this.taxAmount;
      this.repair.total_charges = this.totalAmountWithTax;
      this.repair.profit = this.profit;
      this.repair.total_cost = this.repairCostAmount;
      this.repair.defects = this.currentDefects;
      axios
        .post(route('repair.order.book'), this.repair)
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.loading = false;
          this.$router.push(`/print/repair-order/${response.data.order.uuid}`);
        })
        .catch(() => {
          this.loading = false;
        });
    },
    selectPriority(priority) {
      this.currentPriority = priority;
      this.repair.repair_priority_id = priority.id;
    },
    selectDefect(defect) {
      if (this.currentDefects.includes(defect)) {
        for (let i = 0; i < this.currentDefects.length; i++) {
          if (this.currentDefects[`${i}`] === defect) {
            this.currentDefects.splice(i, 1);
          }
        }
      } else {
        this.currentDefects.push(defect);
      }
    },
    getDefects(device) {
      this.loading = true;
      this.currentDefects = [];
      this.repair.device_id = device.id;
      axios
        .post(route('repair.defect.list'), {
          device_id: this.repair.device_id,
        })
        .then((response) => {
          this.defectList = response.data;
          this.loading = false;
          this.progress = 48;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getDevices(brand) {
      this.loading = true;
      this.defectList = [];
      this.currentDefects = [];
      this.repair.brand_id = brand.id;
      axios
        .post(route('repair.device.list'), { brand_id: this.repair.brand_id, device_type_id: this.device_type_id })
        .then((response) => {
          this.deviceList = response.data;
          this.loading = false;
          this.progress = 32;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getTax() {
      axios
        .get(route('repair.get.tax'))
        .then((response) => {
          this.tax = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getBrandAndPriorityList() {
      this.loading = true;
      this.deviceList = [];
      this.defectList = [];
      axios
        .get(route('repair.brand.list'))
        .then((response) => {
          this.brandList = response.data.brands;
          this.priorityList = response.data.priorities;
          this.getTax();
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    bookingInit(type) {
      this.currentBrand = null;
      this.repair.brand_id = null;
      this.getBrandAndPriorityList();
      this.progress = 16;
    },
  },
};
</script>
